import { useState, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';

import { Widget, WidgetBody } from '@@/Widget';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';

import { DockerSetupFormFields } from './DockerSetupFormFields';
import { DockerSetupFormValues } from './types';
import { useDockerSetupInitialValues } from './useDockerSetupInitialValues';
import { createDockerSetupValidationSchema } from './dockerSetupValidationSchema';

interface Props {
  /** Form submission handler */
  onSubmit: (values: DockerSetupFormValues) => Promise<void>;
  /** Form mode - affects button labels and validation behavior */
  mode: 'create' | 'edit';
  /** Existing policy for edit mode - provides initial values */
  policy?: TypedPolicy<unknown>;
  /** Wizard back navigation - only used in create mode */
  onBack?: () => void;
  /** Whether back is enabled - only used in create mode */
  canGoBack?: boolean;
}

/**
 * Docker Setup Policy Form Component
 *
 * Used by:
 * - PolicyCreateView (step 2): mode="create" with wizard navigation props
 * - ConfigTab (edit view): mode="edit" without wizard navigation
 */
export function DockerSetupPolicyForm({
  onSubmit,
  mode,
  policy,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useDockerSetupInitialValues(policy);
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createDockerSetupValidationSchema(nameValidation),
    [nameValidation]
  );

  return (
    <Formik<DockerSetupFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <DockerSetupPolicyInnerForm
        mode={mode}
        policyId={policy?.Id ?? null}
        originalEnvironmentGroupIds={originalEnvironmentGroupIds}
        onBack={onBack}
        canGoBack={canGoBack}
      />
    </Formik>
  );
}

/**
 * Inner component that has access to Formik context.
 * Renders Widget with form fields and PolicyFormFooter.
 */
function DockerSetupPolicyInnerForm({
  mode,
  policyId,
  originalEnvironmentGroupIds,
  onBack,
  canGoBack,
}: {
  mode: 'create' | 'edit';
  policyId: number | null;
  originalEnvironmentGroupIds: Array<number>;
  onBack?: () => void;
  canGoBack?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<DockerSetupFormValues>();

  // Timezone state for change window configuration
  const [timeZone, setTimeZone] = useState('UTC');

  return (
    <Form className="form-horizontal">
      <Widget>
        <WidgetBody>
          {mode === 'create' && (
            <PolicyConfigurationFormSection policyType="setup-docker" />
          )}
          <DockerSetupFormFields
            formData={values}
            onChange={(key, value) => setFieldValue(key, value)}
            timeZone={timeZone}
            onChangeTimeZone={setTimeZone}
          />
          {/* Edit mode: footer inside Widget */}
          {mode === 'edit' && (
            <PolicyFormFooter
              mode={mode}
              policyType="setup-docker"
              policyId={policyId}
              originalEnvironmentGroupIds={originalEnvironmentGroupIds}
            />
          )}
        </WidgetBody>
      </Widget>

      {/* Create mode: footer outside Widget with StickyFooter */}
      {mode === 'create' && (
        <PolicyFormFooter
          mode={mode}
          policyType="setup-docker"
          policyId={policyId}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      )}
    </Form>
  );
}
